import { useQuery } from "@tanstack/react-query";
import { engineError } from "@/lib/engine-error";
import type { AccountPnl } from "@/lib/profitability-api";
import { queryKeys } from "@/lib/query";

/**
 * The account's profit-and-loss statement.
 *
 * Its own query rather than a slice of `useAccountDetail`, and `enabled` is the
 * reason: the statement lives behind a tab, and the twelve lines across four
 * years should not be fetched for a reader who never opens it.
 */
export function useAccountPnl(accountId: string, range?: string, enabled = true) {
  return useQuery({
    // The range is part of the key for the same reason it is on the detail
    // query: the year columns are fixed, but `range_total` is not, and a cached
    // "Last quarter" statement served under "Last 12 months" would be wrong in
    // exactly the column the reader came for.
    queryKey: [...queryKeys.accountDetail(accountId), "pnl", range ?? "default"],
    queryFn: async (): Promise<AccountPnl> => {
      const response = await fetch(
        `/api/accounts/${encodeURIComponent(accountId)}/pnl${range ? `?range=${range}` : ""}`,
      );
      if (!response.ok) {
        throw await engineError(response, "Could not load this account's P&L");
      }
      return response.json();
    },
    enabled: enabled && accountId.length > 0,
    // Month-end close data, same as the detail query.
    staleTime: 5 * 60 * 1000,
  });
}
